import React from 'react';
import proptypes from 'prop-types';
import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  LoadingButton,
  Typography,
  IconButton,
  TextField
} from '@one-thd/sui-atomic-components';
import { ArrowBack, Close } from '@one-thd/sui-icons';
import { useFormContext, Controller } from 'react-hook-form';
import { ShareProjectDescription } from '../ShareProjectDescription';
import { EntireProject } from '../EntireProject';
import { GroupsShared } from '../GroupsShared';
import { triggerAnalytics } from '../../helpers';
import {
  SHARE_YOUR_PROJECT_STEP,
  STR_2,
  CANCEL_BUTTON,
  X_BUTTON,
  BACK_BUTTON
} from '../../constants';

const {
  bool,
  func,
  shape,
  string,
  arrayOf,
  number
} = proptypes;

export const RecipientDetails = ({
  onClose,
  onBack,
  isLoading,
  itemGroups,
  itemCount
}) => {
  const {
    control,
    formState: { errors: formErrors },
    getValues
  } = useFormContext();

  const [whatShared, pricingChoice, groupsShared] = getValues([
    'whatShared',
    'pricingChoice',
    'groupsShared'
  ]);

  const handleOnClose = (action) => {
    triggerAnalytics(SHARE_YOUR_PROJECT_STEP, { step: STR_2, action });
    onClose();
  };

  const handleOnBack = () => {
    triggerAnalytics(SHARE_YOUR_PROJECT_STEP, { step: STR_2, action: BACK_BUTTON });
    onBack();
  };

  const groupCount = itemGroups?.length || 0;

  return (
    <>
      <DrawerHeader
        startAdornment={(
          <Button aria-label="back" variant="ghost" startIcon={ArrowBack} onClick={handleOnBack}>
            Back
          </Button>
        )}
        endAdornment={
          <IconButton aria-label="close" onClick={() => handleOnClose(X_BUTTON)} icon={Close} />
        }
      >
        <DrawerTitle>Share Your Project</DrawerTitle>
      </DrawerHeader>
      <DrawerBody>
        <div className="sui-gap-6 sui-flex sui-flex-col">
          <ShareProjectDescription />
          <div className="sui-gap-4 sui-flex sui-flex-col">
            <Typography variant="h5">What do you want to share?</Typography>
            {whatShared === 'entire-project' && (
              <EntireProject itemCount={itemCount} groupCount={groupCount} />
            )}
            {whatShared === 'groups' && (
              <GroupsShared itemGroups={itemGroups} groupsShared={groupsShared} />
            )}
          </div>
          <hr />
          <div className="sui-gap-4 sui-flex sui-flex-col">
            <Typography variant="h5">Do you want to share pricing?</Typography>
            {pricingChoice === 'yes' && <Typography>Yes, share pricing</Typography>}
            {pricingChoice === 'no' && <Typography>No, hide pricing</Typography>}
          </div>
          <hr />
          <div className="sui-gap-4 sui-flex sui-flex-col">
            <Typography variant="h5">Who do you want to share with?</Typography>
            <Controller
              name="recipients"
              control={control}
              render={({ field }) => {
                const {
                  ref, value, name, onChange, onBlur, ...rest
                } = field;

                const recipientError = Array.isArray(formErrors.recipients)
                  ? formErrors.recipients.find(Boolean)
                  : formErrors.recipients;

                return (
                  <TextField
                    name={name}
                    status={formErrors.recipients ? 'error' : undefined}
                    statusMessage={recipientError?.message || ''}
                    required
                    onChange={onChange}
                    onBlur={onBlur}
                    label="Recipient's Email"
                    fullWidth
                    helpMessage="Enter up to 10 emails separated by a comma."
                    value={value}
                    InputProps={{ inputProps: { 'data-testid': 'share-recipient-email', ...rest } }}
                  />
                );
              }}
            />
            <Controller
              name="message"
              control={control}
              render={({ field }) => {
                const {
                  ref, value, name, onChange, onBlur, ...rest
                } = field;

                const helpMessage = 'Your message will be individually sent to each email you provide. '
                  + 'Collaborators will also see this message alongside the shared items on your project page.';

                return (
                  <TextField
                    name={name}
                    status={formErrors.message ? 'error' : undefined}
                    statusMessage={formErrors.message?.message}
                    multiline
                    minRows={5}
                    required
                    onChange={onChange}
                    onBlur={onBlur}
                    label="Message"
                    fullWidth
                    helpMessage={helpMessage}
                    value={value}
                    InputProps={{ inputProps: { 'data-testid': 'share-recipient-message', ...rest } }}
                  />
                );
              }}
            />
          </div>
        </div>
      </DrawerBody>
      <DrawerFooter>
        <div className="sui-flex sui-flex-col sui-gap-4">
          <LoadingButton data-testid="share-send-button" loading={isLoading} fullWidth type="submit">
            Send
          </LoadingButton>
          <Button fullWidth variant="secondary" onClick={() => handleOnClose(CANCEL_BUTTON)}>
            Cancel
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
};

RecipientDetails.propTypes = {
  onClose: func.isRequired,
  onBack: func.isRequired,
  isLoading: bool.isRequired,
  itemCount: number.isRequired,
  itemGroups: arrayOf(
    shape({ id: string, groupName: string, products: arrayOf(shape({ id: string })) })
  ).isRequired
};

RecipientDetails.defaultProps = {};
